import { ref, watch, WatchStopHandle } from 'vue'

import {
  CTX,
  LogLevel,
  Shell,
  TRepository,
} from '@sennder/microfrontend-registry'
import { ISennTmsSharedData } from '@sennder/senn-node-microfrontend-interfaces'
import { MicroFrontendLogger } from '@sennder/shell-utilities'
import { deepToRaw } from '@sennder/shell-utilities'

import { getRouteLocationName } from '@/modules/utils'
import router from '@/router'
import { getMicrofrontendData } from '@/router/routes'
import {
  AppAnalyticsProvider,
  wrapWithDeprecationWarning,
} from '@/services/analyticsProvider'
import { loggerInstance } from '@/services/logger'
import { getStateCallbacks, getStateProviders } from '@/store'
import { routes } from './routes'
import { ENV } from '@/common/config'

export const shellLogLevel = ENV === 'dev' ? LogLevel.ALL : LogLevel.OFF

export const shell = ref<Shell<ISennTmsSharedData>>()

export const getRegistryMfContext = async (
  mf: TRepository
): Promise<Readonly<CTX<ISennTmsSharedData>>> => {
  const routeName = getRouteLocationName(router.currentRoute.value)

  const route = Object.values(routes).find((r) => r.name === routeName)

  const npmName = mf.meta.fml.npmName

  const logger: MicroFrontendLogger = new MicroFrontendLogger(
    {
      codeOwners: mf.meta.codeOwners,
      module: npmName,
    },
    () => loggerInstance
  )

  const analyticsProvider: AppAnalyticsProvider = new AppAnalyticsProvider(
    route?.context.analytics || {
      module: npmName,
      submodule: npmName,
    }
  )

  const context: CTX<ISennTmsSharedData> = {
    data: deepToRaw(getMicrofrontendData(npmName)),
    callbacks: getStateCallbacks(),
    providers: {
      ...getStateProviders(),
      logger,
      // @ts-expect-error - segment is deprecated, but we still want to provide it to micro-frontends that use it until we remove it
      segment: wrapWithDeprecationWarning(analyticsProvider),
      analytics: analyticsProvider,
    },
    hooks: [
      (_, mfInstance) => {
        /* TODO: what's the better way to discover "pages"?
          Currently a lot of widgets still return syncChildRouter for no good reason
         */
        const isPage = !!mfInstance?.syncChildRouter

        const watchHandles: WatchStopHandle[] = []

        if (isPage) {
          // sync child router with parent router
          watchHandles.push(
            watch(
              () => router.currentRoute.value,
              (to) => {
                mfInstance?.syncChildRouter?.(to.fullPath)
              },
              {
                immediate: true,
              }
            )
          )
        }

        return () => {
          if (watchHandles.length) {
            for (const unwatch of watchHandles) {
              unwatch()
            }
            watchHandles.length = 0
          }
        }
      },
    ],
  }

  return context
}
